import $ from 'jquery';
import _forEach from 'lodash/forEach';
import _isNaN from 'lodash/isNaN';
import { SELECTORS } from '../constants/selectors';
import { GAME_ATTACK_NAMES } from '../constants/gameAttackConstants';

/**
 * @property UI_SETTINGS_MODAL_TEMPLATE
 * @type {string}
 * @final
 */
const UI_DIALOG_ATTACK_INFORMATION_TEMPLATE = `
    <div class="option-dialog dialog">
        <p class="dialog-title">ADS-B Attacks Information</p>
        <div class="dialog-attack-information-container"></div>
        <div class="dialog-body nice-scrollbar"></div>
    </div>`;

/**
 * @property UI_DIALOG_FOOTER_TEMPLATE
 * @type {string}
 * @final
 */
const UI_DIALOG_ATTACK_INFORMATION_TEXT = `
    <p class="dialog-text">
        Welcome to openScope simulator with cyberattacks from ADS-B. <br>
        There are currently 10 different types of states an aircraft can be in. <br> <br>
        0. Regular - The aircraft is not affected by any attacks <br>
        1. Non responsive - The aircraft is not responding to any commands <br>
        2. Position altering - The aircraft is sending false position data <br>
        3. False information - The aircraft is sending false altitude and velocity information <br>
        4. Standing still - The aircraft appears to stop moving for periods of time <br>
        5. False squawk - The aircraft is sending invalid or emergency squawk codes <br>
        6. False heading - A fake aicraft changes bearing at a given interval <br>
        7. Duplicate aircraft - A fake aircraft is created with the same information as a real aircraft <br>
        8. Impersonate aircraft - A real aircraft takes the callsign of another aircraft <br>
        9. Sybil attack - A main sybil (real aircraft) creates a swarm of nodes (non-responsive fake aicraft) with stolen information <br>
        11. Flooding - There are multiple non-responsive aircrafts in view <br>
        12. GPS Jamming - The aircraft positioning is jammed in a yellow (low-powered) or red (high-powered) area. This results in the aircraft showing a false position to their true one. In a low-powered zone, the location accuracy is low, resulting in a large position error. In the high-powered zone, the aircraft cannot get a new position <br> <br>

        To learn more about the simulator and testing, please explore our tutorial page <a href=\"/tutorial\" target=\"_blank\">here</a>. <br> <br>

        These types of attacks originated from the current weaknesses of ADS-B.<br>
        <a target="_blank" style="color:orange" href="https://urn.kb.se/resolve?urn=urn:nbn:se:liu:diva-204496">
        Please see our paper regarding the weaknesses of ADS-B for more information.</a>
    </p>
`;

/**
 * @property UI_DIALOG_FOOTER_TEMPLATE
 * @type {string}
 * @final
 */
const UI_DIALOG_FOOTER_TEMPLATE = '<div class="dialog-footer">Attacks!</div>';

/**
 * @property UI_OPTION_CONTAINER_TEMPLATE
 * @type {string}
 * @final
 */
const UI_DIALOG_ATTACK_INFORMATION_AIRPLANE_TEMPLATE = `
    <div class="dialog-attack-information-body">
        <div></div>
        <p></p>
    </div>`;

/**
 * @class AttacksInformationController
 */
export default class AttacksInformationController {
    constructor($element) {
        /**
         * Root DOM element
         *
         * @property $element
         * @type {jquery|HTML Element}
         * @default $element
         */
        this.$element = $element;

        /**
         * Dialog DOM element
         *
         * @property $dialog
         * @type {jquery|HTML Element}
         * @default null
         */
        this.$dialog = null;

        /**
         * Dialog's body DOM element
         *
         * @property $dialogBody
         * @type {jquery|HTML Element}
         * @default null
         */
        this.$dialogBody = null;

        this.init();
    }

    /**
     *
     * @for AttacksInformationController
     * @method init
     * @chainable
     */
    init() {
        this.$dialog = $(UI_DIALOG_ATTACK_INFORMATION_TEMPLATE);
        this.$dialogBody = this.$dialog.find(SELECTORS.DOM_SELECTORS.DIALOG_BODY);

        this.$dialogBody.append(UI_DIALOG_ATTACK_INFORMATION_TEXT);

        const $attackInformationContainer = this.$dialog.find(SELECTORS.DOM_SELECTORS.DIALOG_ATTACK_INFORMATION_CONTAINER);

        this._populateAttackInformationContainer($attackInformationContainer);

        this.$dialog.append(this._buildVersionTemplate());
        this.$element.append(this.$dialog);

        return this;
    }

    /**
     * Returns whether the airport selection dialog is open
     *
     * @for AttacksInformationController
     * @method isDialogOpen
     * @return {boolean}
     */
    isDialogOpen() {
        return this.$dialog.hasClass(SELECTORS.CLASSNAMES.OPEN);
    }

    /**
    * @for AttacksInformationController
    * @method toggleDialog
    */
    toggleDialog() {
        this.$dialog.toggleClass(SELECTORS.CLASSNAMES.OPEN);
    }

    /**
     * Populates the container with attack information
     *
     * @for AttacksInformationController
     * @method _populateAttackInformationContainer
     * @param $container {JQuery|HTML Element}
     * @private
     */
    _populateAttackInformationContainer($container) {
        Object.entries(GAME_ATTACK_NAMES).forEach(([key, attack]) => {
            for (let i = 0; i < attack.name.length; i++) {
                const $attackDescription = this._attackDescriptionBuilder(`${key} - ${attack.name[i]}`, attack.color[i]);
                $container.append($attackDescription);
            }
        });
    }

    _attackDescriptionBuilder(attackName, attackColor) {
        const $container = $(UI_DIALOG_ATTACK_INFORMATION_AIRPLANE_TEMPLATE);

        $container.find('div').html(this._aircraftIconBuilder(attackColor));
        $container.find('p').text(attackName);

        return $container;
    }

    _aircraftIconBuilder(color) {
        const template = `
        <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	    viewBox="0 0 122.88 122.88" style="enable-background:new 0 0 122.88 122.88;" xml:space="preserve">
        <g>
	        <path style="fill-rule:evenodd;clip-rule:evenodd;fill:${color};" d="M16.63,105.75c0.01-4.03,2.3-7.97,6.03-12.38L1.09,79.73c-1.36-0.59-1.33-1.42-0.54-2.4l4.57-3.9
		        c0.83-0.51,1.71-0.73,2.66-0.47l26.62,4.5l22.18-24.02L4.8,18.41c-1.31-0.77-1.42-1.64-0.07-2.65l7.47-5.96l67.5,18.97L99.64,7.45
		        c6.69-5.79,13.19-8.38,18.18-7.15c2.75,0.68,3.72,1.5,4.57,4.08c1.65,5.06-0.91,11.86-6.96,18.86L94.11,43.18l18.97,67.5
		        l-5.96,7.47c-1.01,1.34-1.88,1.23-2.65-0.07L69.43,66.31L45.41,88.48l4.5,26.62c0.26,0.94,0.05,1.82-0.47,2.66l-3.9,4.57
		        c-0.97,0.79-1.81,0.82-2.4-0.54l-13.64-21.57c-4.43,3.74-8.37,6.03-12.42,6.03C16.71,106.24,16.63,106.11,16.63,105.75
		        L16.63,105.75z"/>
        </g>
        </svg>
        `;
        return template;
    }

    /**
     * Build the html for the simulator version psuedo-option.
     *
     * @for AttacksInformationController
     * @method _buildVersionTemplate
     * @return {JQuery|HTML element}
     */
    _buildVersionTemplate() {
        const $container = $(UI_DIALOG_FOOTER_TEMPLATE);

        $container.text(`openScope: ADS-B Attacks by Gustav Lindahl, Anton Blåberg, Joakim Thorn, Alex Wahlgren, Axel Boström, Oliver Börjesson, Jon Svensson Magnusson and Zebastian Ulander`);

        return $container;
    }
}
